// Condición de lo devuelto en una venta cancelada: lo declarado dañado y el resto en buen estado.
import type { SaleLine, SaleReturnCondition } from '@/api/services/retail-wizard/contract';
import { returnablePiecesOf, type Translate } from '@/shared/patterns/CancelSale';

export interface ReturnedPiece {
    key: string;
    label: string;
    condition: 'GOOD' | 'DAMAGED';
    identifier?: string;
    /** Unidades de un SKU por cantidad; ausente en una pieza con identificador. */
    qty?: number;
    reason?: string;
}

/** Piezas de la venta con su condición; lo que no se declaró volvió en buen estado. */
export function returnedPiecesOf(
    lines: SaleLine[] | undefined,
    conditions: SaleReturnCondition[] | undefined,
    t: Translate,
): ReturnedPiece[] {
    const declared = conditions ?? [];
    const { pieces, quantities } = returnablePiecesOf(lines, t);
    const result: ReturnedPiece[] = [];

    for (const { sku, label, soldQty } of quantities) {
        const damaged = declared.filter(
            (c) => c.sku === sku && c.identifier === undefined && c.condition === 'DAMAGED',
        );
        const damagedQty = Math.min(
            damaged.reduce((total, c) => total + (c.qty ?? 0), 0),
            soldQty,
        );
        if (damagedQty > 0) {
            const reason = damaged.find((c) => c.reason)?.reason;
            result.push({
                key: `${sku}#DAMAGED`,
                label,
                condition: 'DAMAGED',
                qty: damagedQty,
                ...(reason ? { reason } : {}),
            });
        }
        if (soldQty > damagedQty) {
            result.push({
                key: `${sku}#GOOD`,
                label,
                condition: 'GOOD',
                qty: soldQty - damagedQty,
            });
        }
    }

    for (const { sku, identifier, label } of pieces) {
        const match = declared.find((c) => c.sku === sku && c.identifier === identifier);
        result.push({
            key: `${sku}#${identifier}`,
            label,
            identifier,
            condition: match?.condition ?? 'GOOD',
            ...(match?.reason ? { reason: match.reason } : {}),
        });
    }
    return result;
}
